import { CSSProperties, JSX, memo } from 'preact/compat'
import { useState } from 'preact/hooks'
import ErrorTooltip from './ErrorTooltip'

type InputTextStyle = {
  font_size?: number
  letter_spacing?: string
  text_color: string
  font_family: string
  font_weight: string
}

export type InputStyle = {
  border_size: number
  border_style: string
  border_color: string
  border_top_left: number
  border_top_right: number
  border_bottom_left: number
  border_bottom_right: number
  bg_color: string
  text: InputTextStyle
  label: InputTextStyle
}

type Props = {
  type?: string
  value: string
  name: string
  autoComplete?: string
  onChange: (value: string) => void
  onBlur?: (e: JSX.TargetedFocusEvent<HTMLInputElement>) => void
  options: InputStyle
  placeholder: string
  strictPattern?: string
  errorText?: string | null
  rightButton?: boolean
  readOnly?: boolean
}

const Input = (props: Props) => {
  const { options } = props
  const [focused, setFocused] = useState(false)

  // const onChange = (e: JSX.TargetedEvent<HTMLInputElement>) => {
  const onChange = (e: JSX.TargetedEvent<HTMLInputElement>) => {
    const value =
      props.strictPattern && !e.currentTarget.value.match(props.strictPattern) ? props.value : e.currentTarget.value

    props.onChange(value)
  }

  const styles: CSSProperties = {
    width: '100%',
    lineHeight: 1,
    position: 'relative'
  }

  const labelStyles: CSSProperties = {
    boxSizing: 'border-box',
    display: 'block',
    width: '100%',
    position: 'relative',
    letterSpacing: '0.45px'
  }

  const inputStyles: CSSProperties = {
    width: '100%',
    height: 50,
    paddingTop: 15,
    paddingRight: props.rightButton ? 55 : 12,
    paddingBottom: 0,
    paddingLeft: 12,
    boxSizing: 'border-box',
    outline: 'none',
    boxShadow: 'none',
    fontSize: options.text.font_size || 14,
    letterSpacing: options.text.letter_spacing || 'normal',
    border: `${options.border_size}px ${options.border_style} rgba(${options.border_color})`,
    borderTopLeftRadius: options.border_top_left,
    borderTopRightRadius: options.border_top_right,
    borderBottomRightRadius: options.border_bottom_left,
    borderBottomLeftRadius: options.border_bottom_right,
    backgroundColor: `rgba(${options.bg_color}`,
    color: `rgba(${options.text.text_color})`,
    fontFamily: options.text.font_family,
    fontWeight: options.text.font_weight
  }

  const spanStyles: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 12,
    boxSizing: 'border-box',
    fontSize: options.label.font_size || 14,
    letterSpacing: options.label.letter_spacing || 'normal',
    paddingTop: 18,
    paddingRight: 0,
    paddingBottom: 18,
    paddingLeft: 0,
    pointerEvents: 'none',
    color: `rgba(${options.label.text_color})`,
    fontFamily: options.label.font_family,
    fontWeight: options.label.font_weight,
    transition: 'font-size 200ms, padding 200ms',
    zIndex: 1
  }

  if (props.value || focused) {
    spanStyles.fontSize = 9
    spanStyles.paddingTop = 7
    spanStyles.textTransform = 'uppercase'
  }

  const onBlur = (e: JSX.TargetedFocusEvent<HTMLInputElement>) => {
    setFocused(false)

    if (typeof props.onBlur === 'function') {
      props.onBlur(e)
    }
  }

  const onFocus = () => {
    setFocused(true)
  }

  return (
    <div style={styles} className="wf_reset wf_input_wrapper">
      <label style={labelStyles} className="wf_reset wf_input_label">
        <input
          name={props.name}
          autoComplete={props.autoComplete}
          style={inputStyles}
          className={`wf_reset wf_input ${props.errorText ? 'wf_error' : ''}`}
          value={props.value || ''}
          type={props.type || 'text'}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          readOnly={props.readOnly}
        />
        <span style={spanStyles} className="wf_reset wf_input_span">
          {props.placeholder}
        </span>
      </label>
      {props.errorText ? <ErrorTooltip text={props.errorText} /> : null}
    </div>
  )
}

export default memo(Input)
